import { Button, Grid, Spin, Tooltip } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { TbArrowsMaximize, TbRotate2, TbRotateClockwise2, TbZoomIn, TbZoomOut } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { effectiveScale, rotate, rotatedSize, stepZoom, zoomLabel } from '../Helpers/diskImageZoom'

import type { DiskImageZoom, Size } from '../Helpers/diskImageZoom'
import type { PointerEvent } from 'react'

import styles from './DiskFileViewer.module.css'

interface DiskImageViewProps {
    url: string
    name: string
    onError: () => void
}

/**
 * Зображення: «вписати» за замовчуванням, 100 %, «+»/«−» і колесо миші,
 * поворот на 90°. Збільшене перетягується мишею. Масштаб і поворот —
 * локальний стан показу (новий файл починає з «вписати»), розрахунок —
 * `Helpers/diskImageZoom`.
 */
export const DiskImageView = ({ url, name, onError }: DiskImageViewProps) => {
    const compact = Grid.useBreakpoint().md === false
    const scrollRef = useRef<HTMLDivElement>(null)
    const [natural, setNatural] = useState<Size | null>(null)
    const [box, setBox] = useState<Size>({ width: 0, height: 0 })
    const [zoom, setZoom] = useState<DiskImageZoom>('fit')
    const [rotation, setRotation] = useState(0)

    const scale = natural ? effectiveScale(zoom, natural, box, rotation) : 1
    const scaleRef = useRef(scale)
    scaleRef.current = scale

    // Новий файл — з чистого аркуша
    useEffect(() => {
        setNatural(null)
        setZoom('fit')
        setRotation(0)
    }, [url])

    // Розмір видимої області — для «вписати»
    useEffect(() => {
        const element = scrollRef.current
        if (!element) return undefined

        const measure = (): void => setBox({ width: element.clientWidth - 32, height: element.clientHeight - 32 })
        const observer = new ResizeObserver(measure)

        measure()
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    // Колесо масштабує, а не прокручує: слухач не пасивний, щоб скасувати прокрутку
    useEffect(() => {
        const element = scrollRef.current
        if (!element) return undefined

        const onWheel = (event: WheelEvent): void => {
            if (event.deltaY === 0) return
            event.preventDefault()
            setZoom(stepZoom(scaleRef.current, event.deltaY < 0 ? 1 : -1))
        }

        element.addEventListener('wheel', onWheel, { passive: false })

        return () => element.removeEventListener('wheel', onWheel)
    }, [])

    // Перетягування збільшеного зображення
    const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null)

    const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
        const element = scrollRef.current
        if (!element || event.pointerType !== 'mouse' || event.button !== 0) return

        drag.current = { x: event.clientX, y: event.clientY, left: element.scrollLeft, top: element.scrollTop }
        element.setPointerCapture(event.pointerId)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
        const element = scrollRef.current
        const start = drag.current
        if (!element || !start) return

        element.scrollLeft = start.left - (event.clientX - start.x)
        element.scrollTop = start.top - (event.clientY - start.y)
    }

    const onPointerUp = (): void => {
        drag.current = null
    }

    const shown = natural ? rotatedSize(natural, rotation) : null

    return (
        <>
            <div className={classNames(styles['tools'])} role="toolbar" aria-label="Масштаб зображення">
                <Tooltip title="Зменшити">
                    <Button
                        type="text"
                        icon={<TbZoomOut />}
                        aria-label="Зменшити"
                        onClick={() => setZoom(stepZoom(scale, -1))}
                    />
                </Tooltip>
                <span className={classNames(styles['zoomValue'])} data-testid="disk-viewer-zoom">
                    {zoomLabel(scale)}
                </span>
                <Tooltip title="Збільшити">
                    <Button
                        type="text"
                        icon={<TbZoomIn />}
                        aria-label="Збільшити"
                        onClick={() => setZoom(stepZoom(scale, 1))}
                    />
                </Tooltip>
                <Button
                    type={zoom === 'fit' ? 'default' : 'text'}
                    icon={<TbArrowsMaximize />}
                    aria-label="Вписати"
                    onClick={() => setZoom('fit')}
                >
                    {compact ? null : 'Вписати'}
                </Button>
                <Button type={zoom === 1 ? 'default' : 'text'} onClick={() => setZoom(1)}>
                    100 %
                </Button>
                <Tooltip title="Повернути ліворуч">
                    <Button
                        type="text"
                        icon={<TbRotate2 />}
                        aria-label="Повернути ліворуч"
                        onClick={() => setRotation((value) => rotate(value, -90))}
                    />
                </Tooltip>
                <Tooltip title="Повернути праворуч">
                    <Button
                        type="text"
                        icon={<TbRotateClockwise2 />}
                        aria-label="Повернути праворуч"
                        onClick={() => setRotation((value) => rotate(value, 90))}
                    />
                </Tooltip>
            </div>
            <div
                ref={scrollRef}
                className={classNames(styles['imageScroll'])}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                data-testid="disk-viewer-image-scroll"
            >
                {natural === null && (
                    <div className={classNames(styles['center'])}>
                        <Spin />
                    </div>
                )}
                <div
                    className={classNames(styles['imageBox'])}
                    style={
                        shown
                            ? { width: shown.width * scale, height: shown.height * scale }
                            : { width: 0, height: 0, overflow: 'hidden' }
                    }
                >
                    <img
                        key={url}
                        src={url}
                        alt={name}
                        className={classNames(styles['image'])}
                        draggable={false}
                        onLoad={(event) =>
                            setNatural({
                                width: event.currentTarget.naturalWidth,
                                height: event.currentTarget.naturalHeight,
                            })
                        }
                        onError={onError}
                        style={
                            natural
                                ? {
                                      width: natural.width * scale,
                                      height: natural.height * scale,
                                      transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                                  }
                                : { visibility: 'hidden' }
                        }
                        data-testid="disk-viewer-image"
                    />
                </div>
            </div>
        </>
    )
}
